import { MOTION } from '@/components/common/motion';
import { CircleNotch } from '@phosphor-icons/react';
import type { ButtonHTMLAttributes, FC, ReactNode } from 'react';

/**
 * How much weight a button carries.
 *
 * Only one `primary` should be visible at a time — it is the single dark mark on an otherwise
 * neutral page, so a second one costs the first its meaning.
 */
export type ActionButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost';

/**
 * Each variant carries its own ring colour.
 *
 * It cannot live in the shared base: two ring-colour utilities on one element are settled by the
 * order they appear in the generated stylesheet, not by the order they appear in `class`, so a
 * `danger` button would get whichever of the two Tailwind emitted last.
 */
const VARIANT_CLASSES: Record<ActionButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary/90 focus-visible:ring-primary/40',
  secondary:
    'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 focus-visible:ring-primary/40',
  ghost: 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-primary/40',
  danger: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600/40',
  'danger-ghost': 'text-gray-500 hover:bg-red-50 hover:text-red-600 focus-visible:ring-primary/40',
};

/** Shared by every variant: the ring geometry, the press, and the disabled state. */
const BASE_CLASSES = [
  'inline-flex items-center justify-center gap-2 rounded-lg font-semibold',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
  'active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50',
  MOTION,
].join(' ');

interface ActionButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ActionButtonVariant;
  /** Leading icon. Omit it and the button is label-only. */
  icon?: ReactNode;
  /** Swaps the icon for a spinner and blocks interaction. */
  loading?: boolean;
  /** Square icon-only sizing; the caller still has to supply an `aria-label`. */
  iconOnly?: boolean;
  /**
   * `sm` is the toolbar button. `md` steps the label up to `text-base` — and an icon-only button
   * up to `h-9 w-9` — for the one action a screen is built around.
   */
  size?: 'sm' | 'md';
  className?: string;
  children?: ReactNode;
}

/**
 * The one button in the app.
 *
 * PrimeReact's `Button` arrives wearing the bootstrap-blue theme, which is what put a different
 * blue on every action of every screen. This replaces it wherever the app owns the markup —
 * pages, dialogs, the header — so the accent is decided here and nowhere else.
 */
export const ActionButton: FC<ActionButtonProps> = ({
  variant = 'secondary',
  icon,
  loading = false,
  iconOnly = false,
  size = 'sm',
  className = '',
  children,
  disabled,
  type = 'button',
  ...rest
}) => {
  // An icon-only button is sized by its square, so `size` moves the box; a label button keeps the
  // same box and `size` moves the type. Both default to the toolbar's `sm`.
  const sizeClasses = iconOnly
    ? size === 'md'
      ? 'h-9 w-9 text-sm'
      : 'h-8 w-8 text-sm'
    : size === 'md'
      ? 'px-3 py-2 text-base'
      : 'px-3 py-2 text-sm';
  const leading = loading ? <CircleNotch size={16} className="animate-spin" aria-hidden="true" /> : icon;

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={`${BASE_CLASSES} ${VARIANT_CLASSES[variant]} ${sizeClasses} ${className}`.trim()}
      {...rest}
    >
      {leading}
      {children}
    </button>
  );
};
